/**
 * How a stack stands against the fleet: which template it runs relative to
 * the current one, and whether its hourly heartbeat has gone quiet. One
 * reading for the API, the quiet-account watcher and the portal, so the
 * Customers page, the Focus page and the Connect case never disagree.
 */

const SEMVER = /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z.-]+)?$/;

function comparePrerelease(a: string | undefined, b: string | undefined): -1 | 0 | 1 {
  // A release outranks any of its prereleases: 0.6.0-rc.1 < 0.6.0.
  if (a === undefined || b === undefined) return a === b ? 0 : a === undefined ? 1 : -1;
  const x = a.split(".");
  const y = b.split(".");
  for (let i = 0; i < Math.min(x.length, y.length); i++) {
    const p = x[i]!;
    const q = y[i]!;
    if (p === q) continue;
    const pn = /^\d+$/.test(p);
    const qn = /^\d+$/.test(q);
    if (pn && qn) return Number(p) < Number(q) ? -1 : 1;
    if (pn !== qn) return pn ? -1 : 1;
    return p < q ? -1 : 1;
  }
  return x.length === y.length ? 0 : x.length < y.length ? -1 : 1;
}

/**
 * Semver precedence of two template versions: -1, 0 or 1, or undefined
 * when either is not a version (a missing value, or `unknown` where the
 * API has no current version configured). Build metadata is ignored.
 */
export function compareTemplateVersions(a: string | undefined, b: string | undefined): -1 | 0 | 1 | undefined {
  const x = a === undefined ? null : SEMVER.exec(a.trim());
  const y = b === undefined ? null : SEMVER.exec(b.trim());
  if (!x || !y) return undefined;
  for (const i of [1, 2, 3]) {
    const p = Number(x[i]);
    const q = Number(y[i]);
    if (p !== q) return p < q ? -1 : 1;
  }
  return comparePrerelease(x[4], y[4]);
}

/**
 * Where a stack's template stands against the current one. `ahead` is a
 * version newer than this Central's current - typically a release
 * candidate under test. Only `behind` needs anyone; `unknown` is never
 * called behind.
 */
export type TemplateStanding = "current" | "behind" | "ahead" | "unknown";

export function templateStanding(version: string | undefined, current: string | undefined): TemplateStanding {
  const c = compareTemplateVersions(version, current);
  return c === undefined ? "unknown" : c < 0 ? "behind" : c > 0 ? "ahead" : "current";
}

/** Three missed hourly heartbeats. */
export const QUIET_AFTER_MS = 3 * 60 * 60 * 1000;

/**
 * `reporting` - a heartbeat within three hours. `waiting` - connected, no
 * heartbeat yet, and the three hours since the connectivity test have not
 * run out (the first one arrives within the hour). `quiet` - everything
 * else, including an account that never completed the connectivity test.
 * The three hours run from the last heartbeat, or from the connectivity
 * test where there has been none.
 */
export type HeartbeatState = "reporting" | "waiting" | "quiet";

export function heartbeatState(lastHeartbeatAt: string | undefined, connectivityVerifiedAt: string | undefined, now: number = Date.now()): HeartbeatState {
  const since = lastHeartbeatAt ?? connectivityVerifiedAt;
  if (!since || now - Date.parse(since) > QUIET_AFTER_MS) return "quiet";
  return lastHeartbeatAt ? "reporting" : "waiting";
}

/** How long after the connectivity test the portal says the first poll is under way. */
export const FIRST_POLL_WINDOW_MS = 30 * 60 * 1000;

/**
 * True while the first runs the stack starts at deploy, once its
 * connectivity test is acked (#336), may not have landed: within thirty
 * minutes of `connectivity_verified_at`. An empty poller-fed view says
 * "under way" then, rather than promising up to six hours.
 */
export function firstPollUnderWay(connectivityVerifiedAt: string | undefined, now: number = Date.now()): boolean {
  if (!connectivityVerifiedAt) return false;
  const since = now - Date.parse(connectivityVerifiedAt);
  return since >= -60_000 && since <= FIRST_POLL_WINDOW_MS;
}
